import { CustomSelect, SelectOption } from '@consigli/facade';
import { useGetPackagesQuery, useProjectId } from '@consigli/hooks';
import { DocumentPackageType, DocumentPackageTypeLabels } from '@consigli/types';
import { FC, useMemo } from 'react';
import { FieldValues, useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface SelectVersionPackageProps {
  fieldName: string;
  label: string;
  placeholder: string;
  sourceTypes: DocumentPackageType[];
}

export const SelectVersionPackage: FC<SelectVersionPackageProps> = ({
  fieldName,
  label,
  placeholder,
  sourceTypes,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();

  const { data: allPackages = [] } = useGetPackagesQuery({ projectId });

  const sourceTypeSet = useMemo(() => new Set(sourceTypes), [sourceTypes]);

  const packageOptions = useMemo<SelectOption[]>(
    () =>
      allPackages
        .filter((pkg) => sourceTypeSet.has(pkg.type as DocumentPackageType))
        .map((pkg) => ({
          label: `[${DocumentPackageTypeLabels[pkg.type as DocumentPackageType]}] ${pkg.name}`,
          value: pkg.id,
        })),
    [allPackages, sourceTypeSet],
  );

  const { control } = useFormContext<FieldValues>();
  const { field, fieldState } = useController({
    name: fieldName,
    control,
    rules: {
      validate: (value) => !!value || `${t('version-compare.package-selection-required')}*`,
    },
  });

  const selectedOption = field.value
    ? (packageOptions.find((option) => option.value === field.value) ?? null)
    : null;

  return (
    <div>
      {label && (
        <p className="mt-4 mb-2 text-base font-semibold text-day-neutral-subtle">{label}</p>
      )}
      <CustomSelect
        onChange={(selectedOptions: SelectOption[]) => {
          field.onChange(selectedOptions.length > 0 ? selectedOptions[0].value : null);
        }}
        options={packageOptions}
        placeholder={placeholder}
        value={selectedOption ? [selectedOption] : []}
        noOptionsMessage={t('custom-select.no-options')}
      />
      {fieldState.invalid && (
        <div className="text-red-700 text-sm">{fieldState.error?.message as string}</div>
      )}
    </div>
  );
};
